import React, { useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Label, Textarea, Checkbox, InputCurrency } from "@activecollab/components";
import { Formik, Form } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";
import Angie from "~/utils/angie.jsx";
import AngDropFiles from "../component.ang_drop_files.jsx";
import { CustomFormField } from "../CustomFormField.jsx";
import { useFileHandler } from "../Hooks/index.jsx";
import { Divider } from "../ModalObjects/Divider.js";
import { AttachmentUpload } from "../select_attachments/AttachmentUpload.jsx";
import { SelectAttachments } from "../select_attachments/SelectAttachments.jsx";
import SelectContainer from "../SelectContainer/SelectContainer.js";

export const inputValueName = "value";
export const descriptionName = "summary";
export const categoryName = "category_id";
export const checkboxBillableName = "billable_status";
export const userId = "user_id";
export const attachments = "attachments";

const validationSchema = yup.object().shape({
  [inputValueName]: yup.string(),
  [descriptionName]: yup.string(),
  [categoryName]: yup.number(),
  [checkboxBillableName]: yup.boolean(),
  [userId]: yup.number(),
  [attachments]: yup.object().shape({ filesToAttach: yup.array(), filesToDrop: yup.array() }),
});

const normalizeData = (attributes) => {
  const { attachments, ...rest } = attributes;

  return {
    attach_uploaded_files: attachments.filesToAttach,
    drop_attached_files: attachments.filesToDrop,
    ...rest,
  };
};

export const ExpenseForm = ({
  expenseCategories,
  defaultUserId,
  defaultUserName,
  isBillable,
  canSeeBillable,
  onCancel,
  onSubmit,
  users,
  descriptionValue,
  currencyCode,
  decimalSeparator,
  thousandSeparator,
  categoryId,
  initialValue,
  attachments,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  const { uploading, onUpload, ...fileHandlerRest } = useFileHandler({
    context: "expense",
  });

  const handleSubmit = useCallback(
    (values) => {
      const modified = {
        ...values,
      };

      if (!users.length) {
        delete modified[userId];
      }

      if (!canSeeBillable) {
        delete modified[checkboxBillableName];
      }

      const rawValue = inputRef.current?.value;

      Angie.logger?.info("Expense added", {
        raw_value: rawValue,
        value: modified.value,
        format: rawValue,
        precision: rawValue,
      });

      typeof onSubmit === "function" && onSubmit(normalizeData(modified));
    },
    [onSubmit, users, canSeeBillable]
  );

  const handleOpenFileWindow = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const initialValues = useMemo(() => {
    return {
      [inputValueName]: initialValue,
      [categoryName]: categoryId ?? expenseCategories[0].id,
      [checkboxBillableName]: isBillable,
      [userId]: defaultUserId,
      [descriptionName]: descriptionValue,
      attachments,
    };
  }, [initialValue, categoryId, expenseCategories, isBillable, defaultUserId, descriptionValue, attachments]);

  const handleUpload = useCallback(
    (files) => {
      for (let i = 0; i < files.length; i++) {
        onUpload(files[i]);
      }
    },
    [onUpload]
  );

  const isGreaterThanZero = (val) => val != null && val.trim() !== "" && parseFloat(val) > 0;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, isValid, dirty, errors, setFieldValue, submitForm }) => (
        <>
          <Form className="tw-flex tw-flex-col tw-w-full">
            <div className="tw-relative">
              <div className="tw-flex tw-gap-1 sm:tw-gap-14 tw-flex-col sm:tw-flex-row tw-w-full">
                <div className="tw-w-full sm:tw-w-1/2">
                  <CustomFormField name={inputValueName}>
                    {({ field }) => (
                      <InputCurrency
                        size="biggest"
                        currency={currencyCode}
                        min={0}
                        ref={inputRef}
                        value={field.value}
                        name={field.name}
                        id="c-input"
                        className="tw-w-full tw-mb-4"
                        placeholder={`0${decimalSeparator}00`}
                        autoFocus
                        allowEmptyValue
                        onChange={field.onChange}
                        onEnterKeyPress={async (val) => {
                          await setFieldValue(inputValueName, val);
                          await submitForm();
                        }}
                        invalid={dirty && errors[inputValueName]}
                        decimalSeparator={decimalSeparator}
                        thousandSeparator={thousandSeparator}
                      />
                    )}
                  </CustomFormField>
                  <div>
                    <Label size="small">{t("Description")}</Label>
                    <CustomFormField name={descriptionName}>
                      {({ field }) => (
                        <Textarea
                          name={field.name}
                          defaultValue={field.value}
                          onChange={(e) => field.onChange(e.target.value)}
                          className="expense_summary_textarea tw-w-full tw-h-[96px]"
                        />
                      )}
                    </CustomFormField>
                  </div>
                </div>
                <div className="tw-w-full sm:tw-w-1/2">
                  {users.length ? (
                    <div className="tw-mb-4">
                      <Label size="small">{t("User")}</Label>
                      <CustomFormField name={userId}>
                        {({ field }) => (
                          <SelectContainer
                            options={users}
                            selected={field.value}
                            onChange={field.onChange}
                            placeholder={t("Choose a User")}
                            className="tw-w-full"
                            defaultSelectedName={defaultUserName}
                          />
                        )}
                      </CustomFormField>
                    </div>
                  ) : null}
                  <div className="tw-mb-4">
                    <Label size="small">{t("Category")}</Label>
                    <CustomFormField name={categoryName}>
                      {({ field }) => (
                        <SelectContainer
                          options={expenseCategories}
                          selected={field.value}
                          onChange={field.onChange}
                          placeholder={t("Choose an Expense Category")}
                          className="tw-w-full"
                        />
                      )}
                    </CustomFormField>
                  </div>
                  {canSeeBillable ? (
                    <div className="tw-flex flex-row tw-mr-5 tw-mt-6">
                      <CustomFormField name={checkboxBillableName}>
                        {({ field }) => (
                          <Checkbox
                            id="expense_dialog_checkbox"
                            name={field.name}
                            checked={field.value}
                            onChange={() => field.onChange(!field.value)}
                            className="tw-mr-2"
                          />
                        )}
                      </CustomFormField>
                      <Label
                        weight="regular"
                        className="tw-cursor-pointer"
                        htmlFor="expense_dialog_checkbox"
                      >
                        {t("Billable")}
                      </Label>
                    </div>
                  ) : null}
                </div>
              </div>
              <Divider className="tw-mb-6" />
              <CustomFormField name="attachments">
                {({ field }) => (
                  <SelectAttachments
                    {...fileHandlerRest}
                    uploading={uploading}
                    filesToAttach={field.value.filesToAttach}
                    filesToDrop={field.value.filesToDrop}
                    onChange={field.onChange}
                    onOpenFileWindow={handleOpenFileWindow}
                    className="tw-min-h-[40px] tw-flex tw-flex-col tw-justify-center"
                  />
                )}
              </CustomFormField>
              <AngDropFiles
                onDrop={handleUpload}
                className="tw-p-0 tw--m-0.5"
              />
            </div>
            <div className="tw-flex tw-mt-6">
              <Button
                variant="primary"
                disabled={!(isValid && isGreaterThanZero(inputRef.current?.value)) || isSubmitting}
                type="submit"
                className="tw-mr-3"
              >
                {t("Add Expense")}
              </Button>
              <Button
                variant="secondary"
                onClick={onCancel}
                type="button"
              >
                {t("Cancel")}
              </Button>
            </div>
          </Form>
          <AttachmentUpload
            ref={fileInputRef}
            multiple
            onUpload={onUpload}
          />
        </>
      )}
    </Formik>
  );
};

ExpenseForm.propTypes = {
  expenseCategories: PropTypes.array.isRequired,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      avatarUrl: PropTypes.string,
    })
  ),
  defaultUserId: PropTypes.number,
  isBillable: PropTypes.bool,
  canSeeBillable: PropTypes.bool,
  showRoundingInfo: PropTypes.bool,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  descriptionValue: PropTypes.string,
  isSubmitInProgress: PropTypes.bool,
  defaultUserName: PropTypes.string,
  currencyCode: PropTypes.string,
  decimalSeparator: PropTypes.string,
  thousandSeparator: PropTypes.string,
  categoryId: PropTypes.number,
  initialValue: PropTypes.number,
  attachments: PropTypes.shape({
    filesToAttach: PropTypes.array,
    filesToDrop: PropTypes.array,
  }),
};

ExpenseForm.defaultProps = {
  expenseCategories: [],
  users: [],
  defaultJobTypeId: 1,
  defaultUserId: 0,
  isBillable: false,
  showRoundingInfo: false,
  canSeeBillable: true,
  descriptionValue: "",
  isSubmitInProgress: false,
  decimalSeparator: ".",
  thousandSeparator: ",",
  initialValue: undefined,
  attachments: {
    filesToAttach: [],
    filesToDrop: [],
  },
};
